import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { RiKey2Line } from "react-icons/ri";
import { HiCheckCircle, HiOutlineXCircle } from "react-icons/hi";
import LoaderButton from "components/common/LoaderButton/LoaderButton";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_FAMILY_SET_PASSWORD,
  URL_FAMILY_SET_PASSWORD_INFO,
} from "store/services/URL";
import MobileAppDownloadLinks from "./MobileAppDownloadLinks";

const PASSWORD_RULES = [
  {
    id: "special",
    label: "One special character (!@#$%^&*)",
    test: (value) => /[!@#$%^&*]/.test(value),
  },
  {
    id: "upper",
    label: "One uppercase letter",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: "lower",
    label: "One lowercase letter",
    test: (value) => /[a-z]/.test(value),
  },
  {
    id: "number",
    label: "One number",
    test: (value) => /[0-9]/.test(value),
  },
  {
    id: "length",
    label: "At least 8 characters",
    test: (value) => value.length >= 8,
  },
];

const PasswordRules = ({ password = "" }) => {
  const hasStarted = password.length > 0;

  return (
    <div className='rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3'>
      <p className='text-xs font-medium uppercase tracking-wide text-gray-500'>
        Password requirements
      </p>
      <ul className='mt-2 space-y-1.5'>
        {PASSWORD_RULES.map((rule) => {
          const passed = rule.test(password);
          const tone = !hasStarted
            ? "text-gray-400"
            : passed
            ? "text-emerald-600"
            : "text-red-500";

          return (
            <li
              key={rule.id}
              className={`flex items-center gap-2 text-sm ${tone}`}
            >
              {hasStarted && passed ? (
                <HiCheckCircle className='h-4 w-4 shrink-0' aria-hidden='true' />
              ) : hasStarted ? (
                <HiOutlineXCircle
                  className='h-4 w-4 shrink-0'
                  aria-hidden='true'
                />
              ) : (
                <span
                  className='inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300'
                  aria-hidden='true'
                />
              )}
              <span>{rule.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const FamilySetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [passwordType, setPasswordType] = useState("password");
  const [confirmPasswordType, setConfirmPasswordType] = useState("password");
  const [inviteInfo, setInviteInfo] = useState({
    loading: Boolean(token),
    error: "",
    userType: null,
    appLabel: "App",
  });
  const [request, setRequest] = useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
    successMessage: "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({ mode: "onChange" });
  const watchFormData = watch();
  const password = watchFormData?.password || "";
  const confirmPassword = watchFormData?.password_confirmation || "";

  const passwordMeetsRules = useMemo(
    () => PASSWORD_RULES.every((rule) => rule.test(password)),
    [password]
  );
  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;
  const canSubmit =
    Boolean(token) &&
    !inviteInfo.loading &&
    !inviteInfo.error &&
    passwordMeetsRules &&
    passwordsMatch &&
    !request.isRequested;

  useEffect(() => {
    if (!token) {
      setInviteInfo({
        loading: false,
        error: "This invite link is invalid or incomplete.",
        userType: null,
        appLabel: "App",
      });
      return;
    }

    let cancelled = false;
    setInviteInfo((prev) => ({
      ...prev,
      loading: true,
      error: "",
    }));

    (async () => {
      try {
        const res = await apiGetRequest(
          `${URL_FAMILY_SET_PASSWORD_INFO}?token=${encodeURIComponent(token)}`
        );
        if (cancelled) return;
        const data = res?.data?.data || res?.data || {};
        const userType =
          data.user_type === "participant" ? "participant" : "family";
        setInviteInfo({
          loading: false,
          error: "",
          userType,
          appLabel:
            data.app_label ||
            (userType === "participant" ? "Participant App" : "Family App"),
        });
      } catch (e) {
        if (cancelled) return;
        setInviteInfo({
          loading: false,
          error:
            e?.message ||
            "This invite link is invalid or expired. Ask your provider to send a new invitation.",
          userType: null,
          appLabel: "App",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (data) => {
    if (!token) {
      toast.error("Invalid or missing invite link.");
      return;
    }
    if (inviteInfo.error) {
      toast.error(inviteInfo.error);
      return;
    }
    if (!passwordMeetsRules || data.password !== data.password_confirmation) {
      toast.error("Please meet all password requirements.");
      return;
    }

    setRequest({
      isRequested: true,
      isSuccess: false,
      isFailed: false,
      successMessage: "",
    });
    try {
      const res = await apiPostRequest(URL_FAMILY_SET_PASSWORD, {
        token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      if (res.status !== 200) {
        throw new Error(res?.message || "Could not set password.");
      }
      const successMessage =
        res?.data?.data?.message ||
        `Password set successfully. Open the ${
          inviteInfo.appLabel || "Vertex360"
        } mobile app and sign in with your email.`;
      setRequest({
        isRequested: false,
        isSuccess: true,
        isFailed: false,
        successMessage,
      });

      // Leave any SP/admin session; family/participant sign in on mobile, not web portal
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (_) {
        // ignore storage errors
      }
    } catch (e) {
      setRequest({
        isRequested: false,
        isSuccess: false,
        isFailed: true,
        successMessage: "",
      });
      toast.error(e?.message || "Could not set password.");
    }
  };

  const mobileAppName =
    inviteInfo.appLabel && inviteInfo.appLabel !== "App"
      ? inviteInfo.appLabel
      : inviteInfo.userType === "participant"
      ? "Participant App"
      : "Family App";

  return (
    <div className='min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <div className={`w-full ${request.isSuccess ? "max-w-lg" : "max-w-md"}`}>
        <div className='text-center'>
          <div className='flex justify-center'>
            <div className='rounded-full bg-layer/40 h-20 w-20 flex items-center justify-center'>
              <div className='h-14 w-14 rounded-full bg-blue-200/60 flex items-center justify-center'>
                <RiKey2Line className='h-8 w-8 text-blue-dark' aria-hidden='true' />
              </div>
            </div>
          </div>
          <p className='mt-5 text-xs font-semibold uppercase tracking-wider text-blue-dark/80'>
            {inviteInfo.loading ? "…" : inviteInfo.appLabel}
          </p>
          <h1 className='mt-2 text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight'>
            {request.isSuccess ? "You’re all set" : "Create your password"}
          </h1>
          <p className='mt-2 text-sm sm:text-base text-gray-500 max-w-sm mx-auto'>
            {request.isSuccess
              ? `Your ${mobileAppName} account is ready.`
              : inviteInfo.loading
              ? "Checking your invite link…"
              : inviteInfo.error
              ? "We couldn’t verify this invite link."
              : `You’re invited to Vertex360 ${inviteInfo.appLabel}. Set a secure password to activate access.`}
          </p>
        </div>

        <div className='mt-8 rounded-2xl border border-gray-100 bg-white shadow-sm px-5 py-7 sm:px-8'>
          {request.isSuccess ? (
            <div className='text-center space-y-5'>
              <div className='flex justify-center'>
                <HiCheckCircle
                  className='h-14 w-14 text-emerald-500'
                  aria-hidden='true'
                />
              </div>
              <div className='space-y-2'>
                <p className='text-lg font-semibold text-gray-900'>
                  Your password has been set successfully!
                </p>
                <p className='text-sm text-gray-600 leading-relaxed'>
                  Your account is ready. Download the {mobileAppName} to access
                  your account and manage your services.
                </p>
              </div>
              <MobileAppDownloadLinks />
              <p className='text-sm text-gray-600 leading-relaxed'>
                Sign in with the email from your invitation and the password you
                just created.
              </p>
              <p className='text-xs text-gray-500 leading-relaxed'>
                This page is for setting your password only. Use the mobile app
                to sign in — not the provider portal in your browser.
              </p>
            </div>
          ) : !token || inviteInfo.error ? (
            <div className='rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>
              {inviteInfo.error ||
                "This invite link is invalid or incomplete. Ask your provider to send a new invitation."}
            </div>
          ) : inviteInfo.loading ? (
            <div className='flex justify-center py-8'>
              <LoaderButton
                classes='flex justify-center items-center'
                svgClasses='inline w-6 h-6 text-blue-dark animate-spin'
                textbutton=''
              />
            </div>
          ) : (
            <form className='space-y-5' onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <label
                  htmlFor='family-password'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Password
                </label>
                <div className='relative'>
                  <input
                    id='family-password'
                    type={passwordType}
                    autoComplete='new-password'
                    placeholder='Enter a strong password'
                    className={`did-floating-input pr-11 ${
                      request.isFailed ? "borderRed" : ""
                    }`}
                    {...register("password", {
                      required: "Password is required",
                      validate: () =>
                        passwordMeetsRules ||
                        "Password does not meet requirements",
                    })}
                  />
                  <button
                    type='button'
                    onClick={() =>
                      setPasswordType((t) =>
                        t === "password" ? "text" : "password"
                      )
                    }
                    className='absolute top-1/2 -translate-y-1/2 right-3 text-blue-dark hover:text-blue-800'
                    aria-label={
                      passwordType === "password"
                        ? "Show password"
                        : "Hide password"
                    }
                  >
                    {passwordType === "password" ? (
                      <AiFillEyeInvisible className='h-5 w-5' />
                    ) : (
                      <AiFillEye className='h-5 w-5' />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className='mt-1.5 text-sm text-red-500'>
                    {errors.password.message || "Password is required."}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor='family-password-confirm'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Confirm password
                </label>
                <div className='relative'>
                  <input
                    id='family-password-confirm'
                    type={confirmPasswordType}
                    autoComplete='new-password'
                    placeholder='Re-enter your password'
                    className={`did-floating-input pr-11 ${
                      request.isFailed ? "borderRed" : ""
                    }`}
                    {...register("password_confirmation", {
                      required: "Please confirm your password",
                      validate: (value) =>
                        value === password || "Passwords do not match",
                    })}
                  />
                  <button
                    type='button'
                    onClick={() =>
                      setConfirmPasswordType((t) =>
                        t === "password" ? "text" : "password"
                      )
                    }
                    className='absolute top-1/2 -translate-y-1/2 right-3 text-blue-dark hover:text-blue-800'
                    aria-label={
                      confirmPasswordType === "password"
                        ? "Show confirm password"
                        : "Hide confirm password"
                    }
                  >
                    {confirmPasswordType === "password" ? (
                      <AiFillEyeInvisible className='h-5 w-5' />
                    ) : (
                      <AiFillEye className='h-5 w-5' />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && (
                  <p
                    className={`mt-1.5 text-sm ${
                      passwordsMatch ? "text-emerald-600" : "text-red-500"
                    }`}
                  >
                    {passwordsMatch
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </p>
                )}
                {errors.password_confirmation && !confirmPassword && (
                  <p className='mt-1.5 text-sm text-red-500'>
                    {errors.password_confirmation.message}
                  </p>
                )}
              </div>

              <PasswordRules password={password} />

              <div className='pt-1 space-y-3'>
                {request.isRequested ? (
                  <LoaderButton
                    classes='w-full flex justify-center items-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark'
                    svgClasses='inline mr-3 w-5 h-5 text-white animate-spin'
                    textbutton='Setting password…'
                  />
                ) : (
                  <button
                    type='submit'
                    disabled={!canSubmit}
                    className='w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-dark hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark disabled:opacity-50 disabled:cursor-not-allowed transition'
                  >
                    Set password & continue
                  </button>
                )}

                <Link
                  to='/'
                  className='group w-full flex justify-center items-center gap-2 py-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                    aria-hidden='true'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M11 17l-5-5m0 0l5-5m-5 5h12'
                    />
                  </svg>
                  <span>Back to log in</span>
                </Link>
              </div>
            </form>
          )}
        </div>

        {!request.isSuccess ? (
          <p className='mt-6 text-center text-xs text-gray-400'>
            Link expires after a limited time.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default FamilySetPassword;
